import React, {useState} from 'react';
import {Spin, message, Progress} from 'antd';

import BraftEditor, {BraftEditorProps, ControlType} from 'braft-editor';
// @ts-ignore
import {ContentUtils} from 'braft-utils';
import 'braft-editor/dist/index.css';

import {UploadChangeParam} from 'antd/lib/upload';
import {UploadFile} from 'antd/lib/upload/interface';
import UploadComponent, {uploadType} from './components/upload';

import style from './style.module.scss';
import {RichText} from '../RichText';
import {getDownloadFileName} from '../../Utils/common';

interface EditorProps extends BraftEditorProps {
    value?: string;
    disabled?: boolean;
    canPastedText?: boolean;
    onBlur?: (value: string) => void;
    onChange?: (value: string) => void;
}

const controls: ControlType[] = [
    'bold',
    'italic',
    'underline',
    'separator',
    'link',
    'separator',
    'emoji',
];

const Editor = React.forwardRef<BraftEditor, EditorProps>(
    (
        {value, canPastedText = true, disabled, onChange, onBlur, ...restProps},
        ref,
    ) => {
        const [editorState, setEditorState] = useState(
            BraftEditor.createEditorState(value),
        );
        const [percent, setPercent] = useState(0);

        const handleUploadFile = async (
            res: UploadChangeParam<UploadFile<any>>,
            type: uploadType,
        ) => {
            if (res.file.status === 'uploading') {
                setPercent(Math.floor(res.file.percent || 0));
            }
            if (res.file.status === 'error') {
                setPercent(0);
                message.error(
                    res.file.response && res.file.response.message
                        ? res.file.response.message
                        : '上传失败！',
                );
            }
            if (res.file.status === 'done') {
                setPercent(0);
                const link = getDownloadFileName(
                    res.file.response.key,
                    res.file.name,
                );
                let state;
                if (type === uploadType.File) {
                    state = ContentUtils.insertHTML(
                        editorState,
                        `<a target="_blank" href='${link}'>${res.file.name}</a>`,
                    );
                }
                if (type === uploadType.Image) {
                    state = ContentUtils.insertMedias(editorState, [
                        {type: 'IMAGE', url: link},
                    ]);
                }
                setEditorState(state);
                handleOnBlur(state.toHTML());
            }
        };

        const extendControls: any = [
            'separator',
            {
                key: 'fileUpload',
                type: 'component',
                component: (
                    <UploadComponent
                        type={uploadType.File}
                        onUpload={res => handleUploadFile(res, uploadType.File)}
                    />
                ),
            },
            {
                key: 'imageUpload',
                type: 'component',
                component: (
                    <UploadComponent
                        type={uploadType.Image}
                        onUpload={res =>
                            handleUploadFile(res, uploadType.Image)
                        }
                    />
                ),
            },
        ];

        const handleOnBlur = (value: string) => {
            if (value === '<p></p>') value = '';
            onBlur && onBlur(value);
        };

        const handleOnChange = (editorState: any) => {
            setEditorState(editorState);
            onChange && onChange(editorState.toHTML());
        };

        if (disabled) {
            return (
                <RichText className={style.disabled}>{value || ''}</RichText>
            );
        }

        return (
            <div className={style.root}>
                <Spin
                    size="large"
                    indicator={
                        <Progress
                            className={style.loading}
                            type="circle"
                            percent={percent}
                        />
                    }
                    spinning={percent > 0 && percent < 100}>
                    <BraftEditor
                        ref={ref}
                        value={editorState}
                        controls={controls}
                        onChange={handleOnChange}
                        draftProps={{handlePastedText: () => !canPastedText}}
                        onBlur={(state: any) => handleOnBlur(state.toHTML())}
                        contentStyle={{
                            height: 210,
                            boxShadow: 'inset 0 1px 3px rgba(0,0,0,.1)',
                        }}
                        className={style.editor}
                        extendControls={extendControls}
                        {...restProps}
                    />
                </Spin>
            </div>
        );
    },
);

export {Editor};
